import Dexie, { type Table } from 'dexie';
import type {
  AnimalRecord,
  BuyerRecord,
  DailyMilkUsageRecord,
  EntityName,
  EventRecord,
  ExpenseCategoryRecord,
  ExpenseRecord,
  FileAssetRecord,
  HealthProtocolRecord,
  MilkRecordRecord,
  MutationError,
  PaymentRecord,
  RecurringExpenseRecord,
  ReminderRecord,
  SaleRecord,
} from '@dairy/shared';

export interface OutboxEntry {
  seq?: number;
  mutationId: string;
  entity: EntityName;
  op: 'upsert' | 'delete';
  recordId: string;
  data?: Record<string, unknown>;
  clientTs: string;
  status: 'pending' | 'rejected';
  error?: MutationError;
}

export interface MetaEntry<T = unknown> {
  key: string;
  value: T;
}

/** Device only, never synced (sprint 4 §4). */
export interface FileBlob {
  id: string;
  blob: Blob;
  status: 'pending' | 'uploaded' | 'cached';
}

/**
 * Device database (S1-T12). Schema versions follow the sprints; a new synced table always comes with a new version
 * (sprint 1 Appendix C.3).
 */
export class DairyDB extends Dexie {
  animals!: Table<AnimalRecord, string>;
  events!: Table<EventRecord, string>;
  outbox!: Table<OutboxEntry, number>;
  meta!: Table<MetaEntry, string>;
  milkRecords!: Table<MilkRecordRecord, string>;
  dailyMilkUsage!: Table<DailyMilkUsageRecord, string>;
  reminders!: Table<ReminderRecord, string>;
  healthProtocols!: Table<HealthProtocolRecord, string>;
  buyers!: Table<BuyerRecord, string>;
  sales!: Table<SaleRecord, string>;
  payments!: Table<PaymentRecord, string>;
  expenseCategories!: Table<ExpenseCategoryRecord, string>;
  expenses!: Table<ExpenseRecord, string>;
  recurringExpenses!: Table<RecurringExpenseRecord, string>;
  fileAssets!: Table<FileAssetRecord, string>;
  fileBlobs!: Table<FileBlob, string>;

  constructor(name = 'dairy') {
    super(name);
    // Sprint 1
    this.version(1).stores({
      animals: 'id, tag, status, damId',
      events: 'id, animalId, [animalId+date], type',
      outbox: '++seq, mutationId, status, recordId',
      meta: 'key',
    });
    // Sprint 2
    this.version(2).stores({
      milkRecords: 'id, date, [date+session], animalId, [animalId+date]',
      dailyMilkUsage: 'id, date',
    });
    // Sprint 3
    this.version(3).stores({
      reminders: 'id, animalId, status, dueDate, [status+dueDate]',
      healthProtocols: 'id, type, active',
    });
    // Sprint 4
    this.version(4).stores({
      buyers: 'id, active',
      sales: 'id, date, buyerId, type, animalId',
      payments: 'id, date, buyerId, saleId',
      expenseCategories: 'id, parentId, systemKey',
      expenses: 'id, date, categoryId, animalId, sourceEventId',
      recurringExpenses: 'id',
      fileAssets: 'id, kind',
      fileBlobs: 'id, status',
    });
  }
}

export const db = new DairyDB();

/** Synced tables (everything except outbox, meta and fileBlobs). */
export const SYNCED_TABLES = [
  'animals',
  'events',
  'milkRecords',
  'dailyMilkUsage',
  'reminders',
  'healthProtocols',
  'buyers',
  'sales',
  'payments',
  'expenseCategories',
  'expenses',
  'recurringExpenses',
  'fileAssets',
] as const;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta<T>(key: string, value: T): Promise<void> {
  await db.meta.put({ key, value });
}

/** Remove every farm record from the device (logout or a different user logging in). Keeps the device id. */
export async function wipeFarmData(): Promise<void> {
  const deviceId = await getMeta<string>('deviceId');
  await db.transaction('rw', db.tables, async () => {
    for (const t of db.tables) await t.clear();
    if (deviceId) await db.meta.put({ key: 'deviceId', value: deviceId });
  });
}
